/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import {
  GoogleAuthProvider,
  signInWithCredential,
  signInWithPopup,
  signInWithEmailAndPassword,
  onAuthStateChanged,
} from "firebase/auth";

const provider = new GoogleAuthProvider();

const GOOGLE_WEB_CLIENT_ID =
  "1049837840725-tc4i77p7vv8p222jdo76ar3ruhto2d6u.apps.googleusercontent.com";

let nativeGoogleInitialization;

function initializeNativeGoogle() {
  if (!nativeGoogleInitialization) {
    nativeGoogleInitialization = SocialLogin.initialize({
      google: {
        webClientId: GOOGLE_WEB_CLIENT_ID,
        mode: "online",
      },
    });
  }

  return nativeGoogleInitialization;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [showReviewerLogin, setShowReviewerLogin] = useState(false);
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [reviewerPassword, setReviewerPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      try {
        const profileRef = doc(db, "profile", user.uid);
        const snap = await getDoc(profileRef);

        if (!snap.exists()) {
          await setDoc(
            profileRef,
            {
              name: user.displayName || "Unnamed user",
              photoURL: user.photoURL || "",
              favorites: [],
              bio: "",
              createdAt: new Date(),
            },
            { merge: true },
          );
        }

        navigate("/");
      } catch (error) {
        console.error("Could not initialize BioIndex profile:", error);
        setLoginError("Signed in, but your profile could not be loaded. Please try again.");
      }
    });

    return () => unsub();
  }, [navigate]);

  async function handleLogin() {
    if (loginBusy) return;
    setLoginBusy(true);
    setLoginError("");
    try {
      if (Capacitor.getPlatform() === "android") {
        await initializeNativeGoogle();

        const login = await SocialLogin.login({
          provider: "google",
          options: {},
        });

        const idToken = login.result?.idToken;

        if (!idToken) {
          throw new Error("Google sign-in did not return an ID token.");
        }

        const credential = GoogleAuthProvider.credential(idToken);
        await signInWithCredential(auth, credential);
        return;
      }

      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Google sign-in failed:", error);
      setLoginError("Google sign-in was not completed. Please try again.");
    } finally {
      setLoginBusy(false);
    }
  }

  async function handleReviewerLogin(event) {
    event.preventDefault();
    if (loginBusy || !reviewerEmail.trim() || !reviewerPassword) return;

    setLoginBusy(true);
    setLoginError("");

    try {
      // Password belongs to a pre-created FIREBASE AUTH user, never a Google account.
      // Account creation is deliberately not exposed in this UI.
      await signInWithEmailAndPassword(
        auth,
        reviewerEmail.trim(),
        reviewerPassword,
      );
    } catch (error) {
      console.error("Reviewer sign-in failed:", error.code || error.message);
      setLoginError(
        error.code === "auth/operation-not-allowed"
          ? "Reviewer access is not enabled. Please contact BioIndex support."
          : "Unable to sign in. Check the reviewer email and password.",
      );
    } finally {
      setLoginBusy(false);
    }
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#374049] overflow-y-auto">
      <div className="relative w-full min-h-[100dvh] sm:min-h-0 sm:max-w-sm px-6 sm:px-10 pt-16 pb-10 sm:pb-12 bg-white sm:rounded-3xl sm:shadow-lg sm:border border-[#93CA87]/40 text-center flex flex-col justify-center overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[calc((100dvh-24rem)/2)] before:min-h-8 before:bg-[#374049] before:sm:hidden after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[calc((100dvh-24rem)/2)] after:min-h-8 after:bg-[#374049] after:sm:hidden">
        <div className="relative z-10 w-full max-w-[18rem] mx-auto">
          <h1
            className="text-4xl mb-8 text-[#374049]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            BioIndex
          </h1>

          <p className="text-sm text-[#374049]/70 mb-10">
            {" "}
            Welcome! To access or open your account on BioIndex click the button
            below.
          </p>

          <button
            type="button"
            onClick={handleLogin}
            disabled={loginBusy}
            className="flex items-center justify-center gap-3 w-full py-2.5 rounded-xl bg-[#24C3BE] text-white font-medium tracking-wide hover:bg-[#1faeaa] transition disabled:cursor-not-allowed disabled:opacity-60"
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              className="w-5 h-5 bg-white rounded-full p-[2px]"
            />
            Sign in with Google
          </button>

          <button
            type="button"
            disabled={loginBusy}
            onClick={() => {
              setShowReviewerLogin((open) => !open);
              setLoginError("");
            }}
            aria-expanded={showReviewerLogin}
            aria-controls="bioindex-reviewer-sign-in"
            className="mt-5 text-xs font-semibold text-[#374049]/70 underline underline-offset-4 transition hover:text-[#01767A] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {showReviewerLogin ? "Hide reviewer access" : "Reviewer access"}
          </button>

          {showReviewerLogin && (
            <form
              id="bioindex-reviewer-sign-in"
              onSubmit={handleReviewerLogin}
              className="mt-4 space-y-3 text-left"
            >
              <p className="text-center text-xs leading-5 text-[#374049]/65">
                For pre-authorized BioIndex review accounts.
              </p>
              <div>
                <label
                  htmlFor="reviewer-email"
                  className="mb-1 block text-xs font-semibold text-[#374049]"
                >
                  Email
                </label>
                <input
                  id="reviewer-email"
                  type="email"
                  name="username"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={reviewerEmail}
                  onChange={(event) => setReviewerEmail(event.target.value)}
                  disabled={loginBusy}
                  required
                  className="w-full rounded-xl border border-[#93CA87]/60 bg-white px-3 py-2.5 text-sm text-[#374049] outline-none focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/20 disabled:opacity-60"
                />
              </div>
              <div>
                <label
                  htmlFor="reviewer-password"
                  className="mb-1 block text-xs font-semibold text-[#374049]"
                >
                  Password
                </label>
                <input
                  id="reviewer-password"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={reviewerPassword}
                  onChange={(event) => setReviewerPassword(event.target.value)}
                  disabled={loginBusy}
                  required
                  className="w-full rounded-xl border border-[#93CA87]/60 bg-white px-3 py-2.5 text-sm text-[#374049] outline-none focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/20 disabled:opacity-60"
                />
              </div>
              <button
                type="submit"
                disabled={loginBusy || !reviewerEmail.trim() || !reviewerPassword}
                className="w-full rounded-xl border border-[#374049] bg-[#374049] py-2.5 text-sm font-semibold text-white transition hover:bg-[#2f373e] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loginBusy ? "Signing in..." : "Sign in as reviewer"}
              </button>
            </form>
          )}

          {loginError && (
            <p role="alert" className="mt-4 text-xs leading-5 text-red-700">
              {loginError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
